import React, { useState } from "react";
import { X, Loader2, Layers, SlidersHorizontal } from "lucide-react";
import { TypeBadge, DomainBadge, UrgencyBadge, OwnerBadge } from "./Badges";
import EditableCue from "./EditableCue";
import SegmentedField from "./SegmentedField";
import { useAuth } from "../contexts/AuthContext";
import { urgencyFromDatetime } from "../lib/resurface";
import {
  TYPE_OPTIONS, URGENCY_OPTIONS, HOUSEHOLD_DOMAIN_OPTIONS, TYPE_META, URGENCY_META, DOMAIN_META,
  OWNER_OPTIONS, OWNER_META, VISIBILITY_OPTIONS, VISIBILITY_META,
} from "../lib/meta";

const opts = (arr, meta) => arr.map((v) => ({ value: v, label: meta[v].label }));

function DraftCard({ draft, index, onChange, onRemove, removable, partnerName }) {
  const k = draft._key;
  const [adjust, setAdjust] = useState(false);
  const ownerOpts = OWNER_OPTIONS.map((o) => ({
    value: o, label: o === "partner" && partnerName ? partnerName : OWNER_META[o].label,
  }));
  const domainValue = draft.domain === "PROSPECT" ? "WORK" : draft.domain;

  return (
    <div
      className="fade-up rounded-2xl bg-[var(--surface)] border border-[var(--line)] shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-4"
      data-testid={`draft-card-${index}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[var(--ink5)] mb-1">
            Item {index + 1}
          </p>
          <p className="text-[var(--ink)] text-[15px] md:text-base font-medium leading-snug">{draft.interpreted_summary}</p>
          <p className="text-[13px] text-[var(--ink3)] mt-1">"{draft.raw_text}"</p>
        </div>
        {removable && (
          <button
            data-testid={`draft-remove-${index}`}
            onClick={() => onRemove(k)}
            className="shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-[var(--ink4)] hover:text-[var(--pine)] hover:bg-[var(--surface2)] transition-colors"
            title="Remove this item"
          >
            <X size={16} strokeWidth={2} />
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
        {draft.needs_review && (
          <span className="inline-flex items-center rounded-full border border-[var(--pine-line)] bg-[var(--pine-soft)] px-2.5 py-0.5 text-xs font-semibold text-[var(--pine)]" data-testid="draft-review-tag">
            {!draft.suggested_datetime ? "Needs a time" : "Needs review"}
          </span>
        )}
        <UrgencyBadge urgency={draft.urgency} />
        {draft.owner !== "me" && <OwnerBadge owner={draft.owner} name={partnerName} />}
        <TypeBadge type={draft.type} subtle />
        <DomainBadge domain={draft.domain} subtle />
      </div>

      <div className="mt-3">
        <EditableCue
          entry={draft}
          testid={`draft-cue-${index}`}
          onApply={(iso) => onChange(k, { suggested_datetime: iso, urgency: urgencyFromDatetime(iso) })}
        />
      </div>

      <div className="mt-3 pt-3 border-t border-[var(--line2)]">
        <button
          type="button"
          data-testid={`draft-adjust-${index}`}
          onClick={() => setAdjust((a) => !a)}
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-[var(--ink3)] hover:text-[var(--pine)] hover:bg-[var(--line2)] transition-all duration-200"
        >
          <SlidersHorizontal size={13} strokeWidth={1.9} />
          {adjust ? "Hide details" : "Adjust details"}
        </button>

        {adjust && (
          <div className="mt-3 space-y-3 rise-in" data-testid={`draft-adjust-panel-${index}`}>
            <SegmentedField label="Type" testid={`draft-type-${index}`} value={draft.type}
              options={opts(TYPE_OPTIONS, TYPE_META)} onChange={(v) => onChange(k, { type: v })} />
            <SegmentedField label="Timing" testid={`draft-urgency-${index}`} value={draft.urgency}
              options={opts(URGENCY_OPTIONS, URGENCY_META)} onChange={(v) => onChange(k, { urgency: v })} />
            <SegmentedField label="Area" testid={`draft-domain-${index}`} value={domainValue}
              options={opts(HOUSEHOLD_DOMAIN_OPTIONS, DOMAIN_META)} onChange={(v) => onChange(k, { domain: v })} />
            <SegmentedField label="Owner" testid={`draft-owner-${index}`} value={draft.owner || "me"}
              options={ownerOpts} onChange={(v) => onChange(k, { owner: v })} />
            <SegmentedField label="Visibility" testid={`draft-visibility-${index}`} value={draft.visibility || "private"}
              options={opts(VISIBILITY_OPTIONS, VISIBILITY_META)} onChange={(v) => onChange(k, { visibility: v })} />
          </div>
        )}
      </div>
    </div>
  );
}

export default function DraftReview({ drafts, saving, onChange, onRemove, onSave, onDiscard, persisted }) {
  const { user } = useAuth();
  const partnerName = user?.partner?.name;
  const multi = drafts.length > 1;
  return (
    <div data-testid="draft-review">
      <div className="flex items-center gap-2 mb-2">
        <Layers size={16} className="text-[var(--pine)]" strokeWidth={1.75} />
        <span className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--ink3)]">
          {persisted
            ? `Saved · ${drafts.length} ${multi ? "items" : "item"} for review`
            : multi ? `${drafts.length} separate things to review` : "Quick check before I save"}
        </span>
      </div>
      <p className="text-sm text-[var(--ink3)] mb-4">
        {persisted
          ? "Saved. You can review these later: tidy them up now or leave it."
          : multi
            ? "I split these so none of them get lost. Edit, remove, or save them together."
            : "I wasn't fully sure this was a single thought. Confirm it, edit it, or discard."}
      </p>

      <div className="space-y-3">
        {drafts.map((d, i) => (
          <DraftCard
            key={d._key}
            draft={d}
            index={i}
            onChange={onChange}
            onRemove={onRemove}
            removable={persisted || multi}
            partnerName={partnerName}
          />
        ))}
      </div>

      <div className="sticky bottom-[84px] md:bottom-6 mt-5 z-10">
        <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2 rounded-2xl bg-[var(--surface)] backdrop-blur-md border border-[var(--line)] shadow-[0_10px_30px_rgba(20,32,27,0.12)] p-2.5">
          {!persisted && (
            <button
              data-testid="draft-discard-btn"
              onClick={onDiscard}
              className="w-full sm:w-auto rounded-full px-5 py-3 text-sm font-semibold text-[var(--ink3)] hover:text-[var(--pine)] hover:bg-[var(--surface2)] text-center transition-colors"
            >
              Discard
            </button>
          )}
          <button
            data-testid="draft-save-btn"
            onClick={onSave}
            disabled={saving || drafts.length === 0}
            className="w-full sm:flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[var(--pine)] hover:bg-[var(--pine-dark)] disabled:opacity-40 text-[var(--on-pine)] font-semibold px-6 py-3 text-sm shadow-[0_8px_24px_rgba(44,90,77,0.25)] transition-all duration-200"
          >
            {saving && <Loader2 size={16} className="animate-spin" />}
            {persisted ? "Done reviewing" : `Save ${drafts.length} item${multi ? "s" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
